import { cloudinaryPublicId } from './cloudinaryPublicId'

/**
 * One list of every image the user owns, across both collections.
 *
 * Linear: MYD-467.
 *
 * ---------------------------------------------------------------------------
 * Why two collections exist, and why they are not being merged
 * ---------------------------------------------------------------------------
 *
 * `captureImage` is the field roll: unparented, carrying a `label` and a
 * `capturedAt`, deliberately shot before it is filed. `locationImage` is a
 * filed image, attached to a location, project or note.
 *
 * They model different things, and a capture's `label`, `deviceId` and
 * `usedAt` mean nothing on a filed image. So this merges the VIEW rather than
 * the data: every screen that wants "all my images" asks here instead of
 * knowing about both shapes, which is what stops the gallery and the picker
 * disagreeing about what the user owns.
 */

export type LibraryImageOrigin = 'capture' | 'filed'

export interface LibraryImage {
	id: string
	url: string
	origin: LibraryImageOrigin
	/** `capturedAt` for a capture, `createdAt` for a filed image. ISO string. */
	takenAt: string
	name?: string
	size?: number
	type?: string
	uuid?: string
	/**
	 * What this picture is of, in the user's words: a capture's label, or the
	 * location or project a filed image belongs to. Used as the group heading
	 * and to tell two similar thumbnails apart.
	 */
	context?: string
	/** Captures only. When it was pulled into a note, if it has been. */
	usedAt?: string
}

/**
 * What identifies the FILE, as opposed to the row pointing at it.
 *
 * `uuid` is the Cloudinary `public_id` and is definitive when present. Where a
 * row never kept one (MYD-462 left seven such rows) the id is recovered from
 * the URL, which also normalises away any transformation, so a thumbnail URL
 * and its original resolve to the same asset.
 */
export const assetKeyOf = (row: { uuid?: string; url?: string }): string | null =>
	row.uuid || cloudinaryPublicId(row.url) || row.url?.split('?')[0] || null

/** ISO string, or null when the value is missing or unparseable. */
const isoOf = (value: unknown): string | null => {
	if (!value) return null
	const date = new Date(value as string)
	return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/**
 * When a row has no readable date at all.
 *
 * It sorts last rather than being dropped. An image that exists and can be
 * seen beats one silently withheld because a timestamp is unreadable, which is
 * the same judgement made for a row whose asset cannot be identified, and the
 * whole complaint behind MYD-468 was images that exist but cannot be seen.
 */
const UNDATED = new Date(0).toISOString()

/** A capture row, as `captureImageSlice` holds it. */
export const fromCapture = (row: any): LibraryImage | null => {
	if (!row?._id || !row?.url) return null
	// `capturedAt` is when the photograph was TAKEN, which is the axis a rep
	// thinks in and can differ from when the row was written.
	const takenAt = isoOf(row.capturedAt) || isoOf(row.createdAt) || UNDATED

	return {
		id: String(row._id),
		url: row.url,
		origin: 'capture',
		takenAt,
		name: row.name,
		size: row.size,
		type: row.type,
		uuid: row.uuid,
		context: row.label || undefined,
		usedAt: isoOf(row.usedAt) || undefined,
	}
}

/** A filed row, as `locationImageSlice` holds it. */
export const fromFiled = (row: any): LibraryImage | null => {
	if (!row?._id || !row?.url) return null
	const takenAt = isoOf(row.createdAt) || UNDATED

	// The populated parent, when the endpoint returned one. A bare id has no
	// name to show, so it is left out rather than rendered as an object.
	const location =
		row.locationid && typeof row.locationid === 'object' ? row.locationid.assetName : undefined
	const project =
		row.projectid && typeof row.projectid === 'object' ? row.projectid.projectName : undefined

	return {
		id: String(row._id),
		url: row.url,
		origin: 'filed',
		takenAt,
		name: row.name,
		size: row.size,
		type: row.type,
		uuid: row.uuid,
		context: location || project || undefined,
	}
}

/**
 * Both collections as one list, newest first, one entry per FILE.
 *
 * **Deduplicated by asset, not by row.** A capture pulled into a note exists in
 * both collections pointing at one Cloudinary file (MYD-448 does not
 * re-upload), so without this the same photograph appears twice and looks like
 * a bug.
 *
 * **The capture wins.** It carries the richer story: the label the user typed
 * and the moment the shutter went, against a filed row's "created at". Keeping
 * the filed copy instead would silently downgrade what the user sees.
 */
export const mergeLibraryImages = (captures: any[] = [], filed: any[] = []): LibraryImage[] => {
	const byAsset = new Map<string, LibraryImage>()
	const withoutKey: LibraryImage[] = []

	const add = (image: LibraryImage | null, canReplace: boolean) => {
		if (!image) return
		const key = assetKeyOf(image)
		if (!key) {
			// Nothing to deduplicate against. Keeping it is right: an image the
			// user can see is better than one silently dropped for want of an id.
			withoutKey.push(image)
			return
		}
		if (!byAsset.has(key) || canReplace) byAsset.set(key, image)
	}

	// Filed first, then captures, which are allowed to replace them.
	for (const row of filed) add(fromFiled(row), false)
	for (const row of captures) add(fromCapture(row), true)

	return [...byAsset.values(), ...withoutKey].sort(
		(a, b) => new Date(b.takenAt).getTime() - new Date(a.takenAt).getTime(),
	)
}

export default mergeLibraryImages
